/**
 * `<size-guide>`: links the size chart in snippets/size-guide.liquid to the product form's size
 * radios (`data-form-id`). The guide works without this; it only adds the round trip:
 *
 * - When the dialog opens, the chart marks the size chosen in the buy box.
 * - A click on a row (or its size button) marks that row, and the main button reads
 *   "Select Size L".
 * - That button checks the matching size radio in the product form and fires `change`, so
 *   assets/product-form.js loads the variant as if the shopper had picked it there. The button's
 *   own `command="close"` closes the dialog.
 */

class SizeGuide extends HTMLElement {
  #abort = null;
  #chosen = "";

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const dialog = this.closest("dialog");

    dialog?.addEventListener("command", (event) => {
      if (event.command === "show-modal") this.#sync();
    }, { signal });
    // Safety net for an open without an invoker (the theme editor opens it programmatically).
    dialog?.addEventListener("toggle", (event) => {
      if (event.newState === "open") this.#sync();
    }, { signal });

    this.addEventListener("click", (event) => {
      const row = event.target.closest("tbody tr[data-size]");
      if (row && this.contains(row)) this.#mark(row.dataset.size);
    }, { signal });

    this.querySelector("[data-size-guide-select]")?.addEventListener("click", () => this.#apply(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get #sizeRadios() {
    const form = document.getElementById(this.dataset.formId);
    return [...(form?.querySelectorAll('[data-size-option] input[type="radio"]') ?? [])];
  }

  #sync() {
    const checked = this.#sizeRadios.find((radio) => radio.checked);
    this.#mark(checked?.value ?? "");
  }

  #mark(size) {
    this.#chosen = size;
    for (const row of this.querySelectorAll("tbody tr[data-size]")) {
      row.querySelector("[data-size-guide-row]")?.setAttribute("aria-pressed", String(row.dataset.size === size));
    }
    const button = this.querySelector("[data-size-guide-select]");
    if (button && size) button.textContent = this.dataset.labelSelect.replace("[size]", size);
  }

  #apply() {
    if (!this.#chosen) return;
    const radio = this.#sizeRadios.find((input) => input.value === this.#chosen);
    if (!radio || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

customElements.define("size-guide", SizeGuide);
